import { bindingMode } from 'aurelia-binding';
import { autoinject, bindable } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import * as moment from 'moment';
import 'moment-timezone';
import * as numeral from 'numeral';
import { FormattingHelper } from '../../helpers/formatting-helper';
import { AchToken } from '../../models/ach-token';
import { CardToken } from '../../models/card-token';
import { Account, Customer, Payment } from '../../models/customer';
import { ServiceResponse } from '../../models/service-response';
import { SinglePaymentReceiptRouteSettings } from '../../models/single-payment/single-payment-receipt-route-settings';
import { SinglePaymentRouteSettings } from '../../models/single-payment/single-payment-route-settings';
import { TemplateData } from '../../models/template-data';
import { State } from '../../services/global-state';
import { AchAccountTypeEnum, CardPaymentMethod, CheckPaymentMethod, DigitalWalletPaymentMethod, HpfTransactionService, ICustomerAccountToPay, PaymentMethod, PostedInfo, SubmitTransactionRequest, TransactionTypes } from '../../services/hpf-transaction-service';
import { PayrazrService } from '../../services/payrazr';
import { PortalTemplateTypes, TemplateService } from '../../services/template-service';
import { ComponentValidationResult } from '../../smart/components/payment-item-table/validation/component-validation-result';
import { NumberHelper } from '../../smart/helpers/number-helper';
import { AccountPaymentItem } from '../../smart/models/account-payment-item';
import { PaymentItemInitialEnteredAmount } from '../../smart/models/enums';
import { PaymentItemType } from '../../smart/models/enums/payment-item-type';
import { PaymentItemTableConfiguration } from '../../smart/models/payment-item-table-configuration';
import { ApplePaySettings, ApplePayUtil, DigitalWalletType } from '../../models/digital-wallet';
import { MerchantConsumerPortalFeeSettings } from '../../models/merchant-consumer-portal-fee-settings';

@autoinject
export class SinglePayment {
	@bindable({ defaultBindingMode: bindingMode.twoWay }) selectedWalletToCustomer: Customer;
	// standard page-level
	isLoading: boolean = true;
	isSubmitting: boolean = false;
	validationErrors: Array<string>;
	state: string = 'setup';
	confirmationTemplateData: TemplateData;
	confirmationTitle: string;
	error;
	customTemplateName: string;
	confirmationSubmitButtonText: string;

	// output vars from payment form
	calculatedFeeAmount: number;
	calculatedTotalAmount: number;

	// page-specific
	payInFull: boolean;
	minFuture: number;
	maxFuture: number;
	subtotal: number = 0;
	subDiscountAmount: number = 0;
	isSingleFuture: boolean = false;
	isPayOffEarly: boolean = false;
	merchantConsumerPortalFeeSettings: MerchantConsumerPortalFeeSettings = new MerchantConsumerPortalFeeSettings();

	// payment item table
	paymentItemTableConfiguration: PaymentItemTableConfiguration;
	forceRefreshOfData: boolean;
	paymentItemTableViewModel: any;
	selectedPaymentItemsChanged(selectedPaymentItems: PaymentItem[]) {}
	paymentItemsLoaded(paymentItems: PaymentItem[]) {
		this.updateSubtotal();
	}
	enteredAmountChanged(changedItem: PaymentItem) {
		this.updateSubtotal();
	}

	// payment form
	customer: Customer;
	accounts: Account[];
	selectedCustomerAccountKeys: number[];
	bucketKey: number | null;
	paymentInformation: any;
	showConvenienceFeeDisclosureOnPaymentForm: any; // ??????????
	allowSingleFuture: boolean;
	isAch: boolean = false;
	isDigitalWallet: boolean = false;

	get isApplePayPayment(): boolean {
		return this.isDigitalWallet && this.paymentInformation.digitalWalletSettings?.isApplePay();
	}

	constructor(
		private globalState: State,
		private router: Router,
		private payrazrService: PayrazrService,
		private templateService: TemplateService,
		private i18n: I18N,
		private hpfTransactionService: HpfTransactionService,
	) {
	}

	activate(params, routeConfig) {
		if (routeConfig.settings instanceof SinglePaymentRouteSettings) {
			let routeSettings: SinglePaymentRouteSettings = routeConfig.settings as SinglePaymentRouteSettings;
			this.payInFull = routeSettings.payInFull;
			this.isPayOffEarly = routeSettings.isPayOffEarly;
			this.selectedCustomerAccountKeys = routeSettings.selectedCustomerAccountKeys;
		}

		this.customer = this.globalState.customer;

		// get the accounts based on keys passed through route settings
		this.accounts = [];
		this.selectedCustomerAccountKeys.forEach((selectedAccountKey: number) => {
			let selectedAccount: Account = this.globalState.accounts.find((accountFromState: Account) =>
				accountFromState.customerAccountKey === selectedAccountKey
			);
			this.accounts.push(selectedAccount);
		});

		if (this.globalState.isConsumerPortalGuarantorBillingEnabled
			&& this.accounts.length === 1) {
			this.globalState.associatedCustomers.forEach(customer => {
				customer.accounts.forEach(account => {
					if (this.accounts[0].customerAccountKey === account.customerAccountKey) {
						this.customer = customer;
					}
				});
			});
		}

		// payment date restrictions setup
		this.minFuture = 0;

		this.allowSingleFuture = this.getSingleFutureAllowedForSelectedAccounts();
		if (this.isPayOffEarly) {
			this.allowSingleFuture = false;
		}

		// max future days setup
		if (!this.allowSingleFuture) {
			this.maxFuture = 0;
		} else {
			this.maxFuture = Number.MAX_VALUE;

			// loop through all portfolios to get the most restrictive settings (min of max)
			this.accounts.forEach((a: Account) => {
				let portfolio = this.globalState.business.fetchPortfolioSettings(a.portfolioName);
				let merchantMaxFutureDays: number = parseInt(portfolio.merchantSettings.maxFutureDays, 10);
				let portfolioMaxFutureDays: number = parseInt(portfolio.settings.paymentTypes.maxFutureDays, 10);

				if (merchantMaxFutureDays && merchantMaxFutureDays < this.maxFuture) {
					this.maxFuture = merchantMaxFutureDays;
				}

				if (portfolioMaxFutureDays && portfolioMaxFutureDays < this.maxFuture) {
					this.maxFuture = portfolioMaxFutureDays;
				}
			});
		}

		// get fees enabled merchant setting. Right now just grab from the first account since we only allow a
		// single merchant.This would need to change for multi-merchant support
		this.merchantConsumerPortalFeeSettings.feesEnabled = this.globalState.business.fetchPortfolioSettings(this.accounts[0].portfolioName).merchantSettings.feeSettings.serviceFeeEnabled;

		// payment item table initialization
		this.paymentItemTableConfiguration = new PaymentItemTableConfiguration();
		this.paymentItemTableConfiguration.itemType = PaymentItemType.Account;
		this.paymentItemTableConfiguration.selectFirstItemUponLoad = false;
		this.paymentItemTableConfiguration.enableItemSelection = false;
		this.paymentItemTableConfiguration.onlyShowSelected = true;
		this.paymentItemTableConfiguration.showPaymentAmount = true;
		this.paymentItemTableConfiguration.isPaymentAmountEditable = !this.payInFull && !this.isPayOffEarly;
		this.paymentItemTableConfiguration.isPayInFull = this.payInFull;
		this.paymentItemTableConfiguration.isPayOffEarly = this.isPayOffEarly;
		this.paymentItemTableConfiguration.showStatementLink = false;
		this.paymentItemTableConfiguration.showSubtotal = this.accounts.length > 1;
		this.paymentItemTableConfiguration.paymentAmountLabel = this.globalState.business.portalSettings.fields.paymentAmount.label || null;
		this.forceRefreshOfData = false;

		if (this.isPayOffEarly) {
			this.paymentItemTableConfiguration.initialAmountType = PaymentItemInitialEnteredAmount.pendingBalance;
		}

		this.isLoading = false;
	}

	getSingleFutureAllowedForSelectedAccounts(): boolean {
		let allowed: boolean = true;
		this.accounts.forEach((a: Account) => {
			if (!allowed) {
				return;
			}
			let portfolio = this.globalState.business.fetchPortfolioSettings(a.portfolioName);
			let currentPortfolioAllowed: boolean = portfolio.settings.paymentTypes.allowFuture;
			if (!currentPortfolioAllowed) {
				allowed = false;
			}
		});
		return allowed;
	}

	next(event) {
		// payment form errors
		this.validationErrors = event.detail.errors;
		let confirmOk: boolean = true;

		// if no payment form errors, validate other components and the page. Currently the payment form returns a list of errors OR payment info.
		// Now that payment info is needed for external validation, it would be ideal if it were refactored to return both errors AND payment info, so the
		// list of errors could be combined into one, if needed.

		// payment item table errors
		let paymentItemValidationResult: ComponentValidationResult = this.paymentItemTableViewModel.validate();
		if (!paymentItemValidationResult.isValid && paymentItemValidationResult.confirm) {
			// Remove confirmation error message.
			let confirmErrorMessage = paymentItemValidationResult
				.errorMessages.splice(
					paymentItemValidationResult.confirmErrorMessageIndex, 1);

			// Display a confirmation alert
			confirmOk = confirm(
				...confirmErrorMessage);

			// Render remaining validation errors.
			this.validationErrors.push(...paymentItemValidationResult.errorMessages);
		} else if (!paymentItemValidationResult.isValid) {
			this.validationErrors.push(...paymentItemValidationResult.errorMessages);
		}

		// page errors
		if (event.detail.errors.length === 0) {
			this.validationErrors.push(...this.getValidationErrors(event.detail));
		}

		if (this.validationErrors.length === 0 && confirmOk) {
			this.paymentInformation = event.detail;

			this.updateSubtotal();

			let paymentDate = moment(this.paymentInformation.date, 'MM/DD/YYYY');
			let today = moment(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY'), 'MM/DD/YYYY');
			this.isSingleFuture = (paymentDate > today);

			if (this.paymentInformation.isWallet) {
				if (this.paymentInformation.card && this.paymentInformation.card.selectedPaymentMethod) {
					this.paymentInformation.card.cardNumber = this.paymentInformation.card.selectedPaymentMethod.last4ofCard;
				}
				else if (this.paymentInformation.check && this.paymentInformation.check.selectedPaymentMethod) {
					this.paymentInformation.check.accountNumber = this.paymentInformation.check.selectedPaymentMethod.last4ofAccount;
					this.paymentInformation.check.transitNumber = this.paymentInformation.check.selectedPaymentMethod.aba;
				}
			}

			this.getConfirmationTemplateData();

			if (this.isDigitalWallet) {
				if (this.isApplePayPayment) {
					this.setApplePaySettingsFields();
				}
			}

			this.showConfirmation();
		}
	}

	updateSubtotal(): void {
		this.subtotal = this.paymentItemTableViewModel.getTotalEnteredAmountForSelectedItems();
		if (this.payInFull) {
			this.subDiscountAmount = this.paymentItemTableViewModel.getTotalDiscountAmountForSelectedItems();
		}
	}

	getConfirmationTemplateData() {
		let makePaymentText: string = this.i18n.i18next.t('makePayment', 'Make Payment');

		this.confirmationTitle = (this.isSingleFuture) ?
			this.i18n.i18next.t('reviewInfoPressSchedulePayment', 'Please review the following information and press the Schedule Payment button') :
			this.i18n.i18next.t('reviewInfoPressMakePayment', {
				defaultValue: 'Please review the following information and press the Make Payment button.',
				payment: makePaymentText
			});

		this.confirmationSubmitButtonText = (this.isSingleFuture) ?
			this.i18n.i18next.t('schedulePayment', 'Schedule Payment') :
			makePaymentText;

		let customObject: any;

		if (this.paymentInformation.card) {
			// card
			this.isAch = false;
			this.isDigitalWallet = false;
			this.customTemplateName = 'Credit Card Payment Confirmation';
			customObject = {
				date: this.paymentInformation.date,
				name: this.paymentInformation.name,
				address1: this.paymentInformation.addressLine1,
				phone: this.paymentInformation.billingPhone, // todo: is this used?
				email: this.paymentInformation.billingEmail, // todo: is this used?
				zip: this.paymentInformation.zipCode,
				fee: numeral(this.calculatedFeeAmount).format('($0,0.00)'),
				amount: numeral(this.subtotal).format('($0,0.00)'),
				customerAccountNumber: this.accounts[0].referenceId,
				accountNumber: this.accounts[0].referenceId, // backward compatibility
				customerAccountName: this.accounts[0].name,
				merchantZid: this.globalState.business.getMerchantZidAssociatedToPortfolioByPortfolioName(this.accounts[0].portfolioName),
				description: this.paymentInformation.transactionDescription,
				total: numeral(this.calculatedTotalAmount).format('($0,0.00)'),
				cardNumber: this.paymentInformation.card.cardNumber !== 'undefined' ? '***' + FormattingHelper.mask(this.paymentInformation.card.cardNumber) : '',
				expDate: this.paymentInformation.card.expirationDate,
				customerAccountLocationName: (this.accounts[0].bucket) ? this.accounts[0].bucket.displayName : '',
				customerAccountLocationReferenceId: (this.accounts[0].bucket) ? this.accounts[0].bucket.referenceId : '',
				billingPhone: this.globalState.customer.mobilePhone,
				billingEmail: this.globalState.customer.email
			};
		} else if (this.paymentInformation.check) {
			// ach
			this.isAch = true;
			this.isDigitalWallet = false;
			this.customTemplateName = 'eCheck Payment Confirmation';
			customObject = {
				date: this.paymentInformation.date,
				name: this.paymentInformation.name,
				address1: this.paymentInformation.addressLine1,
				phone: this.paymentInformation.billingPhone,
				email: this.paymentInformation.billingEmail,
				zip: this.paymentInformation.zipCode,
				fee: numeral(this.calculatedFeeAmount).format('($0,0.00)'),
				amount: numeral(this.subtotal).format('($0,0.00)'),
				customerAccountNumber: this.accounts[0].referenceId,
				accountNumber: this.accounts[0].referenceId, // backward compatibility
				customerAccountName: this.accounts[0].name,
				merchantZid: this.globalState.business.getMerchantZidAssociatedToPortfolioByPortfolioName(this.accounts[0].portfolioName),
				description: this.paymentInformation.transactionDescription,
				total: numeral(this.calculatedTotalAmount).format('($0,0.00)'),
				bankAccount: this.paymentInformation.check.accountNumber !== 'undefined' ? '***' + FormattingHelper.mask(this.paymentInformation.check.accountNumber) : '',
				checkNumber: this.paymentInformation.check.checkNumber,
				transitNumber: this.paymentInformation.check.transitNumber,
				customerAccountLocationName: (this.accounts[0].bucket) ? this.accounts[0].bucket.displayName : '',
				customerAccountLocationReferenceId: (this.accounts[0].bucket) ? this.accounts[0].bucket.referenceId : '',
				billingPhone: this.globalState.customer.mobilePhone,
				billingEmail: this.globalState.customer.email
			};
		} else if (this.paymentInformation.digitalWalletSettings) {
			// digital wallet
			this.isAch = false;
			this.isDigitalWallet = true;
			this.customTemplateName = 'Digital Wallet Sale';
			customObject = {
				date: this.paymentInformation.date,
				phone: this.paymentInformation.billingPhone, // todo: is this used?
				email: this.paymentInformation.billingEmail, // todo: is this used?
				amount: numeral(this.subtotal).format('($0,0.00)'),
				customerAccountNumber: this.accounts[0].referenceId,
				accountNumber: this.accounts[0].referenceId, // backward compatibility
				customerAccountName: this.accounts[0].name,
				merchantZid: this.globalState.business.getMerchantZidAssociatedToPortfolioByPortfolioName(this.accounts[0].portfolioName),
				description: this.paymentInformation.digitalWalletName,
				total: numeral(this.calculatedTotalAmount).format('($0,0.00)'),
				customerAccountLocationName: (this.accounts[0].bucket) ? this.accounts[0].bucket.displayName : '',
				customerAccountLocationReferenceId: (this.accounts[0].bucket) ? this.accounts[0].bucket.referenceId : '',
				billingPhone: this.globalState.customer.mobilePhone,
				billingEmail: this.globalState.customer.email,
				digitalWalletName: this.paymentInformation.digitalWalletName,
				totalLabel: this.merchantConsumerPortalFeeSettings.feesEnabled ? this.i18n.i18next.t('subTotal', 'Subtotal') + '*' : this.i18n.i18next.t('total', 'Total'),
				showDigitalWalletFeeDisclosure: this.merchantConsumerPortalFeeSettings.feesEnabled
			};
		}

		let accountPayments = this.getAccountsToPayForSubmission();
		const merchantKey = this.globalState.business.getMerchantKeyAssociatedToPortfolioByPortfolioName(this.accounts[0].portfolioName);
		this.confirmationTemplateData = new TemplateData(customObject, merchantKey, accountPayments);
	}

	getMerchantKey(): number {
		const accountPayments = this.getAccountsToPayForSubmission();
		return accountPayments[0].merchantKey; // pick out the merchant key from the first account for now until we support MMIDs
	}

	setApplePaySettingsFields(): void {
		if (!this.paymentInformation.digitalWalletSettings?.isApplePay()) {
			return;
		}

		const applePaySettings: ApplePaySettings = this.paymentInformation.digitalWalletSettings as ApplePaySettings;
		applePaySettings.totalAmount = this.subtotal.toString();

		if (this.isSingleFuture) {
			// recurring requests are set to pending to display 'amount pending' text in modal
			applePaySettings.totalType = 'pending';
			applePaySettings.paymentTiming = 'deferred';
			applePaySettings.deferredPaymentDate = this.paymentInformation.date;
		}

		this.paymentInformation.digitalWalletSettings = applePaySettings;
	}

	submit(event: CustomEvent) {
		console.log('received dispatch apple pay submit', event.detail);

		let saveToWallet = this.paymentInformation.addPaymentMethodToWalletIsSelected;

		if (saveToWallet
			&& this.globalState.isConsumerPortalGuarantorBillingEnabled) {
			// Call save to wallet for a specific account after transaction success.
			this.paymentInformation.addPaymentMethodToWalletIsSelected = false;
		}

		return this.submitTransaction(event)
			.then(() => {
				if (!saveToWallet
					|| !this.globalState.isConsumerPortalGuarantorBillingEnabled
					|| !!this.paymentInformation.digitalWalletSettings) {
					return;
				}

				let customerId: string = this.globalState.customer.id;

				if (this.globalState.isConsumerPortalGuarantorBillingEnabled) {
					customerId = this.selectedWalletToCustomer.id;
				}

				if (!!this.paymentInformation.card) {
					let cardToken = new CardToken();
					cardToken.cardholderName = this.paymentInformation.name;
					cardToken.cardNumber = this.paymentInformation.card.cardNumber;
					cardToken.expirationDate = this.paymentInformation.card.expirationDate;
					cardToken.zipCode = this.paymentInformation.zipCode;

					return this.payrazrService.addCardPaymentMethod(cardToken, false, customerId);
				} else if (!!this.paymentInformation.check) {
					let achToken = new AchToken();
					achToken.accountName = this.paymentInformation.name;
					achToken.accountNumber = this.paymentInformation.check.accountNumber;
					achToken.aba = this.paymentInformation.check.transitNumber;
					achToken.accountType = this.paymentInformation.check.accountType;
					achToken.addressLine1 = this.paymentInformation.check.addressLine1;
					achToken.zipCode = this.paymentInformation.zipCode;

					return this.payrazrService.addAchPaymentMethod(achToken, false, customerId);
				}
			})
			.then(response => {
				let routeSettings: SinglePaymentReceiptRouteSettings = new SinglePaymentReceiptRouteSettings();
				routeSettings.accountPayments = this.getAccountsToPayForSubmission();
				routeSettings.paymentInformation = this.paymentInformation;
				this.router.routes.find((routeConfig) => routeConfig.name === 'single-payment/single-payment-receipt').settings = routeSettings;
				this.router.navigateToRoute('single-payment/single-payment-receipt');

				this.isSubmitting = false;
			})
			.catch(reason => {
				alert('error');
				this.isSubmitting = false;
			});
	}

	submitTransaction(event: CustomEvent): Promise<any> {
		// Use hpf api for pay today and single futures for card and check.
		// Continue to use old portal api methods for pay off early since the logic is slightly different.
		// Update: The above still stands, with a new exception:
		// Use HPF API for pay off early with DigitalWallet.
		let useHpfApi = !this.isPayOffEarly || this.isDigitalWallet;

		if (useHpfApi) {
			return this.submitToHpfApi(event)
				.then((response: ServiceResponse) => {
					if (response.isSuccessful) {
						// response was successful
						if (response.value.isSuccessful) {
							// transaction or plan creation was successful
							if (this.isSingleFuture) {
								this.paymentInformation.planId = response.value.paymentId;
							} else {
								this.paymentInformation.pnref = response.value.paymentId;
							}
							this.paymentInformation.portalTemplateType = this.templateService.getPortalTemplateType(this.paymentInformation, this.isSingleFuture);

							if (response.value.hasException) {
								this.paymentInformation.customMessage = response.value.resultMessage;
							}
						} else {
							// transaction or plan creation failed
							this.paymentInformation.portalTemplateType = PortalTemplateTypes.TransactionFailed;
							this.paymentInformation.resultCode = response.value.resultCode;
							this.paymentInformation.resultMessage = response.value.resultMessage;
						}
					} else {
						// response was not successful
						this.paymentInformation.portalTemplateType = PortalTemplateTypes.TransactionFailed;
						this.paymentInformation.resultCode = response.resultMessage; // maybe need to tweak later to pass ApiResponse result code through to here
						this.paymentInformation.resultMessage = response.resultMessage;
					}

					this.paymentInformation.customerAccountNumber = this.accounts[0].displayName;
					this.paymentInformation.savedPaymentMethodToWallet = this.paymentInformation.addPaymentMethodToWalletIsSelected;
					this.paymentInformation.isFuturePayment = this.isSingleFuture;

					// complete digital wallet session
					if (this.isApplePayPayment) {
						const isSuccessful: boolean = response.isSuccessful && response.value.isSuccessful && !response.value.hasException;
						ApplePayUtil.completeSession(event, isSuccessful);
					}
				})
				.catch(error => {
					alert('error');
					this.isSubmitting = false;
					// complete digital wallet session
					if (this.isApplePayPayment) {
						ApplePayUtil.completeSession(event, false);
					}
					// this.transactionError = error;
				});
		} else {
			return this.submitToApi()
				.then(result => {
					if (result.response.isSuccessful) {
						this.paymentInformation.pnref = result.response.pnref;
						this.paymentInformation.planId = result.response.planId;
						this.paymentInformation.merchantKey = result.response.merchantKey;
						this.paymentInformation.portalTemplateType = this.templateService.getPortalTemplateType(this.paymentInformation, this.isSingleFuture);
					} else {
						this.paymentInformation.portalTemplateType = PortalTemplateTypes.TransactionFailed;
						this.paymentInformation.resultCode = result.response.resultCode;
						this.paymentInformation.resultMessage = result.response.message;
					}

					this.paymentInformation.customerAccountNumber = this.accounts[0].displayName;
					this.paymentInformation.savedPaymentMethodToWallet = this.paymentInformation.addPaymentMethodToWalletIsSelected;
					this.paymentInformation.isFuturePayment = this.isSingleFuture;

					if (result.response.hasException) {
						this.paymentInformation.customMessage = result.response.message;
					}
				})
				.catch(error => {
					alert('error');
					this.isSubmitting = false;
					// this.transactionError = error;
				});
		}
	}

	submitToHpfApi(event: CustomEvent): Promise<ServiceResponse> {
		let transactionRequest = new SubmitTransactionRequest();
		transactionRequest.transactionType = (this.isSingleFuture) ? TransactionTypes.SingleFuture : TransactionTypes.Sale;
		transactionRequest.amount = this.subtotal;
		transactionRequest.accountPayments = this.getAccountsToPayForSubmission();
		transactionRequest.merchantKey = transactionRequest.accountPayments[0].merchantKey; // pick out the merchant key from the first account for now until we support MMIDs
		transactionRequest.paymentDate = this.paymentInformation.date;
		transactionRequest.postedInfo = new PostedInfo();
		transactionRequest.postedInfo.userType = 'C'; // always consumer user type when coming from portal
		transactionRequest.description = this.isDigitalWallet ? this.paymentInformation.digitalWalletName : this.paymentInformation.transactionDescription;
		if (this.paymentInformation.addPaymentMethodToWalletIsSelected) {
			transactionRequest.saveToWalletForCustomerKey = this.customer.customerKey;
		}

		if (this.paymentInformation.isWallet) {
			transactionRequest.paymentMethod = this.getWalletInfoForSubmission();
		} else if (this.paymentInformation.card) {
			transactionRequest.paymentMethod = this.getCardInfoForSubmission();
		} else if (this.paymentInformation.check) {
			transactionRequest.paymentMethod = this.getCheckInfoForSubmission();
		} else if (this.paymentInformation.digitalWalletSettings) {
			transactionRequest.paymentMethod = this.getDigitalWalletInfoForSubmission(event?.detail);
		}

		this.paymentInformation.merchantKey = transactionRequest.merchantKey;

		transactionRequest.relatedCustomerKey = this.globalState.customer.customerKey;

		transactionRequest.isPayOffEarly = this.isPayOffEarly;

		if (this.isPayOffEarly) {
			transactionRequest.paymentPlanId = this.accounts[0].paymentPlans
				.filter(x => x.totalPayments >= 1)
				.sort((a, b) => a.paymentDate - b.paymentDate)
				.pop()
				.id;
		}

		return this.hpfTransactionService.submitTransaction(transactionRequest);
	}

	private getAccountsToPayForSubmission() {
		let accountsToPay: ICustomerAccountToPay[] = [];
		let selectedPaymentItems: AccountPaymentItem[] = this.paymentItemTableViewModel.getSelectedItems();
		selectedPaymentItems.forEach((i: AccountPaymentItem) => {
			accountsToPay.push({
				customerAccountKey: i.itemKey,
				paymentAmount: NumberHelper.parseCurrency(i.enteredAmount),
				discountAmount: this.getDiscountAmountForAccountPaymentItem(i),
				merchantKey: i.merchantKey,
				paymentBalanceType: i.paymentBalanceType,
				paymentLimitSettings: i.paymentLimitSettings,
				minPaymentDue: i.minPaymentDue
			} as ICustomerAccountToPay);
		});
		return accountsToPay;
	}

	getDiscountAmountForAccountPaymentItem(paymentItem: AccountPaymentItem): number {
		return this.payInFull
			? NumberHelper.parseCurrency((paymentItem.initialAmount * paymentItem.discount / 100).toFixed(2))
			: this.isPayOffEarly
				? this.getDiscountAmountFromFirstCustomerAccount()
				: 0;
	}

	private getDiscountAmountFromFirstCustomerAccount(): number {
		return NumberHelper.parseCurrency((this.accounts[0].balance - this.accounts[0].adjustedBalances.pendingBalance).toFixed(2));
	}

	private getWalletInfoForSubmission(): PaymentMethod {
		if (this.paymentInformation.isCardPayment &&
			this.paymentInformation.card &&
			this.paymentInformation.card.selectedPaymentMethod
		) {
			let card: CardPaymentMethod = new CardPaymentMethod();
			card.paymentMethodKey = this.paymentInformation.card.selectedPaymentMethod.cardInfoKey;
			return card;
		} else if (this.paymentInformation.isCheckPayment &&
			this.paymentInformation.check &&
			this.paymentInformation.check.selectedPaymentMethod
		) {
			let check: CheckPaymentMethod = new CheckPaymentMethod();
			check.paymentMethodKey = this.paymentInformation.check.selectedPaymentMethod.checkInfoKey;
			check.checkNumber = this.paymentInformation.check.checkNumber;
			return check;
		}
		return null; // shouldn't get here!
	}

	private getCardInfoForSubmission(): CardPaymentMethod {
		let card: CardPaymentMethod = new CardPaymentMethod();
		// common
		card.customerKey = this.customer.customerKey;
		card.accountName = this.paymentInformation.name;
		card.addressLine1 = this.paymentInformation.addressLine1;
		card.city = this.paymentInformation.billingCity;
		card.state = this.paymentInformation.billingState;
		card.zipCode = this.paymentInformation.zipCode;
		// card-specific
		card.accountNumber = this.paymentInformation.card.cardNumber;
		card.cvv = this.paymentInformation.card.cvv;
		card.expirationMonth = parseInt(this.paymentInformation.card.expirationDate.substring(0, 2), 10);
		card.expirationYear = parseInt('20' + this.paymentInformation.card.expirationDate.substring(2, 4), 10);
		card.isThirdParty = this.paymentInformation.isThirdPartyPayment;
		return card;
	}

	private getCheckInfoForSubmission(): CheckPaymentMethod {
		let check: CheckPaymentMethod = new CheckPaymentMethod();
		// common
		check.customerKey = this.customer.customerKey;
		check.accountName = this.paymentInformation.name;
		check.addressLine1 = this.paymentInformation.addressLine1;
		check.city = this.paymentInformation.billingCity;
		check.state = this.paymentInformation.billingState;
		check.zipCode = this.paymentInformation.zipCode;
		// check-specific
		check.accountNumber = this.paymentInformation.check.accountNumber;
		check.aba = this.paymentInformation.check.transitNumber;
		check.checkNumber = this.paymentInformation.check.checkNumber;
		switch (this.paymentInformation.check.accountType) {
			case 'checking':
				check.achAccountType = AchAccountTypeEnum.PersonalChecking;
				break;
			case 'savings':
				check.achAccountType = AchAccountTypeEnum.PersonalSavings;
				break;
			case 'business':
				check.achAccountType = AchAccountTypeEnum.BusinessChecking;
				break;
		}
		return check;
	}

	getDigitalWalletInfoForSubmission(digitalWalletEventData: any): DigitalWalletPaymentMethod {
		if (digitalWalletEventData === null) {
			return null;
		}

		let digitalWallet: DigitalWalletPaymentMethod = new DigitalWalletPaymentMethod();
		// common
		digitalWallet.customerKey = this.customer.customerKey;
		// digital wallet specific
		if (digitalWalletEventData.digitalWalletType === DigitalWalletType.ApplePay) {
			const applePayPayment: ApplePayJS.ApplePayPayment = digitalWalletEventData.payment;
			digitalWallet.digitalWalletType = DigitalWalletType.ApplePay;

			const applePayPaymentToken = JSON.stringify(applePayPayment.token);
			digitalWallet.authenticationPaymentToken = btoa(applePayPaymentToken);
			digitalWallet.calculatedServiceFee = digitalWalletEventData.calculatedServiceFee;
			digitalWallet.cardType = applePayPayment.token.paymentMethod.type;
			digitalWallet.isDebitCard = applePayPayment.token.paymentMethod.type === 'debit';
			digitalWallet.isCreditCard = applePayPayment.token.paymentMethod.type === 'credit';

			// we require name and postal code
			digitalWallet.accountName = applePayPayment.billingContact?.givenName + ' ' + applePayPayment.billingContact?.familyName;
			digitalWallet.zipCode = applePayPayment.billingContact?.postalCode ?? '';

			console.log('stringified token:', applePayPaymentToken);
			console.log('dw payment method: ', digitalWallet);
		}

		return digitalWallet;
	}

	submitToApi(): Promise<any> {
		if (this.isSingleFuture) {
			// Pay future
			let paymentDate = moment(this.paymentInformation.date, 'MM/DD/YYYY');
			if (this.paymentInformation.card) {
				return this.submitSingleFutureCard(paymentDate);
			} else if (this.paymentInformation.check) {
				return this.submitSingleFutureCheck(paymentDate);
			}
		} else if (this.isPayOffEarly) {
			// Pay off early
			if (this.paymentInformation.card) {
				return this.submitPayOffEarlyCard();
			} else if (this.paymentInformation.check) {
				return this.submitPayOffEarlyCheck();
			}
		} else {
			// pay today
			if (this.paymentInformation.card) {
				return this.submitSingleTodayCard();
			} else if (this.paymentInformation.check) {
				return this.submitSingleTodayCheck();
			}
		}
	}
	submitSingleTodayCheck(): Promise<any> {
		return this.payrazrService.submitCheckPaymentV2(
			this.globalState.customer,
			this.accounts[0],
			this.paymentInformation.name,
			this.paymentInformation.addressLine1,
			this.paymentInformation.billingCity,
			this.paymentInformation.billingState,
			this.paymentInformation.zipCode,
			this.paymentInformation.billingPhone,
			this.paymentInformation.billingEmail,
			null,
			this.paymentInformation.transactionDescription,
			this.paymentInformation.isThirdPartyPayment,
			this.subtotal,
			this.calculatedFeeAmount,
			this.paymentInformation.check,
			null,
			this.paymentInformation.addPaymentMethodToWalletIsSelected,
			this.subDiscountAmount
		);
	}
	submitSingleFutureCheck(paymentDate): Promise<any> {
		return this.payrazrService.submitCheckPaymentPlan(
			this.accounts[0],
			0,
			this.subtotal,
			1,
			paymentDate,
			this.paymentInformation.name,
			this.paymentInformation.addressLine1,
			this.paymentInformation.billingCity,
			this.paymentInformation.billingState,
			this.paymentInformation.billingPhone,
			this.paymentInformation.billingEmail,
			null,
			this.paymentInformation.zipCode,
			this.calculatedFeeAmount,
			this.paymentInformation.transactionDescription,
			this.paymentInformation.isThirdPartyPayment,
			this.paymentInformation.check,
			0,
			false,
			this.paymentInformation.addPaymentMethodToWalletIsSelected,
			this.subDiscountAmount
		);
	}
	submitPayOffEarlyCheck(): Promise<any> {
		// Currently pay off early does not support ach. Add that here
		return null;
	}
	submitSingleTodayCard(): Promise<any> {
		return this.payrazrService.submitCardPaymentV2(
			this.globalState.customer,
			this.accounts[0],
			this.paymentInformation.name,
			this.paymentInformation.addressLine1,
			this.paymentInformation.billingCity,
			this.paymentInformation.billingState,
			this.paymentInformation.zipCode,
			this.paymentInformation.billingPhone,
			this.paymentInformation.billingEmail,
			null,
			this.paymentInformation.transactionDescription,
			this.paymentInformation.isThirdPartyPayment,
			this.subtotal,
			this.calculatedFeeAmount,
			this.paymentInformation.card,
			null,
			this.paymentInformation.addPaymentMethodToWalletIsSelected,
			this.subDiscountAmount
		);
	}
	submitSingleFutureCard(paymentDate): Promise<any> {
		return this.payrazrService.submitCardPaymentPlan(
			this.accounts[0],
			0,
			this.subtotal,
			1,
			paymentDate,
			this.paymentInformation.name,
			this.paymentInformation.addressLine1,
			this.paymentInformation.billingCity,
			this.paymentInformation.billingState,
			this.paymentInformation.billingPhone,
			this.paymentInformation.billingEmail,
			null,
			this.paymentInformation.zipCode,
			this.calculatedFeeAmount,
			this.paymentInformation.transactionDescription,
			this.paymentInformation.isThirdPartyPayment,
			this.paymentInformation.card,
			0,
			false,
			this.paymentInformation.addPaymentMethodToWalletIsSelected,
			this.subDiscountAmount
		);
	}
	submitPayOffEarlyCard(): Promise<any> {
		this.subDiscountAmount = this.getDiscountAmountFromFirstCustomerAccount();

		return this.payrazrService.submitCardPaymentV2ForInstallmentsPayOffEarly(
			this.globalState.customer,
			this.accounts[0],
			this.paymentInformation.name,
			this.paymentInformation.addressLine1,
			this.paymentInformation.billingCity,
			this.paymentInformation.billingState,
			this.paymentInformation.zipCode,
			this.paymentInformation.billingPhone,
			this.paymentInformation.billingEmail,
			this.paymentInformation.transactionDescription,
			this.paymentInformation.isThirdPartyPayment,
			this.paymentInformation.amount,
			this.paymentInformation.fee,
			this.paymentInformation.card,
			this.paymentInformation.addPaymentMethodToWalletIsSelected,
			this.subDiscountAmount
		);
	}

	showConfirmation() {
		this.state = 'confirmation';
	}

	hideConfirmation() {
		this.state = 'setup';
	}

	getValidationErrors(paymentInformation: any): string[] {
		let validationErrors: string[] = [];

		let paymentDate = moment(paymentInformation.date, 'MM/DD/YYYY');
		let minFuture = moment(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY')).add(this.minFuture, 'd').startOf('day');
		let maxFuture = moment(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY')).add(this.maxFuture, 'd').endOf('day');

		if (!paymentDate.isValid()) {
			validationErrors.push(this.i18n.i18next.t('invalidPaymentDate', 'Invalid payment date'));
		}

		if (paymentDate < minFuture) {
			validationErrors.push(this.i18n.i18next.t('postDateCannotBeInPast', 'Post-date cannot be in the past'));
		}
		if (paymentDate > maxFuture) {
			validationErrors.push(this.i18n.i18next.t('postDateCannotBeMoreThan', { defaultValue: `Post-date cannot be more than ${this.maxFuture} day${this.maxFuture > 1 ? 's' : ''} from now`, future: this.maxFuture, sAddition: this.maxFuture > 1 ? 's' : '' }));
		}

		return validationErrors;
	}

	goBack() {
		this.router.navigateToRoute('secure');
	}

	reset() {
		this.validationErrors = new Array();
	}
}
